import { useCallback, useMemo } from "react";

export const getTeamStatus = (team) => {
  if (team.isVerified) return "verified";
  if (!team.scores || team.scores.length === 0) return "pending-scores";
  if (team.scores.some((s) => s.score === null || s.score === undefined))
    return "pending-scores";
  return "pending-verification";
};

// Client-side filtering function
const applyClientSideFilters = (data, filters) => {
  let filteredTeams = [...(data.teams || [])];

  // Apply squad filter
  if (filters.squad && filters.squad !== "all") {
    filteredTeams = filteredTeams.filter(
      (team) => team.squadID === filters.squad
    );
  }

  // Apply date filter
  if (filters.date && filters.date !== "all") {
    filteredTeams = filteredTeams.filter(
      (team) => team.squad?.squadDate === filters.date
    );
  }

  // Apply event filter
  if (filters.event && filters.event !== "all") {
    const selectedEvent = data.events?.find(
      (event) => event.id === filters.event
    );

    filteredTeams = filteredTeams.filter((team) => {
      if (team.eventsID === filters.event) return true;

      if (selectedEvent && team.bowlers) {
        return team.bowlers.some((bowler) => {
          if (bowler.eventEntries && Array.isArray(bowler.eventEntries)) {
            return bowler.eventEntries.some(
              (entry) => entry.name === selectedEvent.eventName
            );
          }
          return false;
        });
      }
      return false;
    });
  }

  // Apply status filter
  if (filters.status && filters.status !== "all") {
    filteredTeams = filteredTeams.filter((team) => {
      const teamStatus = getTeamStatus(team);
      return teamStatus === filters.status;
    });
  }

  // Apply search filter
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();

    if (searchLower.startsWith("team:")) {
      const teamId = searchLower.substring(5);
      filteredTeams = filteredTeams.filter((team) => team.id === teamId);
    } else {
      filteredTeams = filteredTeams.filter(
        (team) =>
          team.teamName?.toLowerCase().includes(searchLower) ||
          team.bowlers?.some((bowler) =>
            bowler.name?.toLowerCase().includes(searchLower)
          )
      );
    }
  }

  return {
    teams: filteredTeams,
    tournament: data?.tournament || null,
    squads: data?.squads || [],
    events: data?.events || [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalTeams: filteredTeams.length,
      hasNextPage: false,
      hasPrevPage: false,
    },
  };
};

export const useTournamentFilters = (data, filters) => {
  // Apply filters and memoize result
  const filteredData = useMemo(() => {
    return applyClientSideFilters(data, filters);
  }, [data, filters]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    return {
      squad: "all",
      event: "all",
      status: "all",
      date: "all",
      search: "",
      page: 1,
    };
  }, []);

  return {
    filteredData,
    clearAllFilters,
    getTeamStatus,
  };
};
